import React, { useState, useEffect, useContext } from 'react';
import { ServerConfigContext } from '../context/ServerConfigContext';
import { createApiService } from '../utils/apiService';
import { transformApiData, getRateColorClass } from '../utils/formatUtils';
import TradingTable from '../components/TradingTable';
import './TradingPages.css';

// 쿠키 유틸리티 함수
const setCookie = (name, value, days = 30) => {
  try {
    const expires = new Date();
    expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
    const cookieValue = encodeURIComponent(JSON.stringify(value));
    document.cookie = `${name}=${cookieValue};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
  } catch (error) {
    console.error('쿠키 저장 오류:', error);
  }
};

const getCookie = (name) => {
  try {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    for(let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) {
        const cookieValue = c.substring(nameEQ.length, c.length);
        const decodedValue = decodeURIComponent(cookieValue);
        return JSON.parse(decodedValue);
      }
    }
    return null;
  } catch (error) {
    console.error('쿠키 불러오기 오류:', error);
    return null;
  }
};

const RealtimePage = () => {
  const { getServer, tableSettings, filterStockData, isConfigLoaded } = useContext(ServerConfigContext);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedTime, setSelectedTime] = useState(new Date().toTimeString().split(' ')[0].substring(0, 5));
  const [timeUnit, setTimeUnit] = useState('분'); // '분', '시', '일'
  const [availableDates, setAvailableDates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tradingData, setTradingData] = useState([]);
  const [allAvailableThemes, setAllAvailableThemes] = useState([]); // 현재 표시된 모든 테마
  const [isSearching, setIsSearching] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [visibleDetails, setVisibleDetails] = useState({
    sector: true,
    per: true,
    pbr: true,
    foreignBuy: true,
    institutionBuy: true,
    individualBuy: true,
    high52Week: true,
    low52Week: true,
    volume52Week: true
  });

  // 기본 차트 설정
  const defaultChartSettings = {
    upColor: '#d32f2f',
    downColor: '#1976d2', 
    wickColor: '#424242',
    wickThickness: 1,
    bodyThickness: 0.8,
    showMA5: true,
    showMA10: true,
    showMA20: true,
    showMA60: false,
    showMA120: false,
    ma5Color: '#ff9800',
    ma10Color: '#4caf50',
    ma20Color: '#9c27b0',
    ma60Color: '#f44336',
    ma120Color: '#795548',
    showGrid: true,
    gridColor: '#e0e0e0'
  };

  // 쿠키에서 차트 설정 불러오기
  const [chartSettings, setChartSettings] = useState(() => {
    const savedSettings = getCookie('realtimePageChartSettings');
    return savedSettings ? { ...defaultChartSettings, ...savedSettings } : defaultChartSettings;
  });
  const [activeTab, setActiveTab] = useState('chart');

  // 종목정보 전체 표시 여부 상태 (PressedPage와 동일한 쿠키 사용)
  const [showDetailsSection, setShowDetailsSection] = useState(() => {
    const savedShowDetails = getCookie('pressedPageShowDetailsSection');
    return savedShowDetails !== undefined ? savedShowDetails : true;
  });

  // setShowDetailsSection 래퍼 함수
  const handleSetShowDetailsSection = (newValue) => {
    setShowDetailsSection(newValue);
  };

  // showDetailsSection 변경 시 쿠키에 저장
  useEffect(() => {
    setCookie('pressedPageShowDetailsSection', showDetailsSection);
  }, [showDetailsSection]);

  // chartSettings 변경 시 쿠키에 저장
  useEffect(() => {
    setCookie('realtimePageChartSettings', chartSettings);
  }, [chartSettings]);

  // 서버에서 조회 가능한 날짜 목록을 가져오는 함수
  const fetchAvailableDates = async () => {
    try {
      console.log('📅 날짜 목록 조회 시작...');
      setLoading(true);
      const server = getServer('stock-api-server');
      
      if (!server) {
        console.error('❌ stock-api-server를 찾을 수 없습니다.');
        setIsInitializing(false);
        return;
      }

      if (!server.isActive) {
        console.error('❌ stock-api-server가 비활성 상태입니다.');
        setIsInitializing(false);
        return;
      }

      console.log('🔗 API 서버:', server.baseUrl);
      const apiService = createApiService(server);
      console.log('🚀 /api/v1/ranks API 호출 중...');
      const dateList = await apiService.get('/api/v1/ranks');
      console.log('✅ 날짜 목록 조회 성공:', dateList);
      
      setAvailableDates(dateList);
      
      // 현재 선택된 날짜가 조회 가능한 날짜 목록에 없으면 가장 최근 날짜로 설정
      if (dateList.length > 0) {
        const currentSelectedDateFormatted = selectedDate.replace(/-/g, '');
        if (!dateList.includes(currentSelectedDateFormatted)) {
          const latestDate = dateList.sort().reverse()[0]; // 가장 최근 날짜
          const formattedDate = `${latestDate.substring(0, 4)}-${latestDate.substring(4, 6)}-${latestDate.substring(6, 8)}`;
          setSelectedDate(formattedDate);
        }
      }
    } catch (error) {
      console.error('조회 가능한 날짜를 가져오는 중 오류 발생:', error);
    } finally {
      setLoading(false);
    }
  };

  // 선택된 날짜가 조회 가능한 날짜인지 확인하는 함수
  const isDateAvailable = (date) => {
    const formattedDate = date.replace(/-/g, '');
    return availableDates.includes(formattedDate);
  };

  // 날짜 변경 핸들러 (조회 가능한 날짜만 허용)
  const handleDateChange = (newDate) => {
    if (isDateAvailable(newDate)) {
      setSelectedDate(newDate);
    }
  };

  // 시간이 유효한 범위인지 확인 (09:00 ~ 15:30)
  const isTimeInValidRange = (timeString) => {
    const [hours, minutes] = timeString.split(':').map(Number);
    const timeInMinutes = hours * 60 + minutes;
    const startTime = 9 * 60; // 09:00
    const endTime = 15 * 60 + 30; // 15:30
    
    return timeInMinutes >= startTime && timeInMinutes <= endTime;
  };

  // 선택된 날짜가 오늘인지 확인 (현지 시간 기준)
  const isSelectedDateToday = (dateString) => {
    const now = new Date();
    const year = now.getFullYear();
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    const day = now.getDate().toString().padStart(2, '0');
    const today = `${year}-${month}-${day}`;
    
    return dateString === today;
  };

  // 현재 시간보다 이후인지 확인 (오늘 날짜일 때)
  const isTimeAfterNow = (timeString) => {
    const now = new Date();
    const [hours, minutes] = timeString.split(':').map(Number);
    const selectedTime = new Date();
    selectedTime.setHours(hours, minutes, 0, 0);
    
    return selectedTime > now;
  };

  // 시간 유효성 검사 (날짜에 종속)
  const isTimeValid = (dateString, timeString) => {
    // 1단계: 기본 거래 시간 범위 검사 (09:00~15:30)
    if (!isTimeInValidRange(timeString)) {
      return false;
    }

    // 2단계: 날짜에 따른 시간 제한 검사
    if (isSelectedDateToday(dateString)) {
      // 오늘 날짜: 09:00 ~ 현재 시각까지만 선택 가능
      if (isTimeAfterNow(timeString)) {
        return false;
      }
    }
    // 과거 날짜: 09:00 ~ 15:30 모든 시간 선택 가능

    return true;
  };

  // 시간 변경 핸들러 - 유효성 검사 없이 바로 상태 반영
  const handleTimeChange = (newTime) => {
    setSelectedTime(newTime);
  };

  // 서버 설정이 로드된 후에 데이터 초기화
  useEffect(() => {
    let retryCount = 0;
    const maxRetries = 5;
    const retryInterval = 300;

    const initializeData = async () => {
      try {
        console.log('🚀 RealtimePage 초기화 시작...', { isConfigLoaded, retryCount });
        
        // 서버 설정이 실제로 사용 가능한지 확인
        const server = getServer('stock-api-server');
        if (server && server.isActive) {
          console.log('✅ RealtimePage - 서버 설정 확인됨, 데이터 초기화 시작');
          await fetchAvailableDates();
          return;
        }
        
        // 서버 설정이 없거나 비활성 상태일 때 재시도
        if (retryCount < maxRetries) {
          retryCount++;
          console.warn(`⚠️ RealtimePage - 서버 설정 대기 중 (${retryCount}/${maxRetries})...`);
          setTimeout(initializeData, retryInterval);
        } else {
          console.error('❌ RealtimePage - 서버 설정 초기화 최대 재시도 실패');
          setIsInitializing(false);
        }
      } catch (error) {
        console.error('❌ RealtimePage - 초기화 중 오류:', error);
        setIsInitializing(false);
      }
    };

    // isConfigLoaded가 true가 되거나 일정 시간 후 시작
    if (isConfigLoaded) {
      initializeData();
    } else {
      // 설정 로드를 기다리지만 최대 2초 후에는 강제 시작
      const forceInitTimeout = setTimeout(() => {
        console.warn('⚠️ RealtimePage - 설정 로드 대기 시간 초과, 강제 초기화 시작');
        initializeData();
      }, 2000);

      // isConfigLoaded가 true가 되면 타이머 정리
      if (isConfigLoaded) {
        clearTimeout(forceInitTimeout);
      }

      return () => clearTimeout(forceInitTimeout);
    }
  }, [isConfigLoaded, getServer]);

  // availableDates가 업데이트되면 초기 데이터 로드
  useEffect(() => {
    if (availableDates.length > 0 && isDateAvailable(selectedDate) && isTimeValid(selectedDate, selectedTime)) {
      fetchTradingData(selectedDate, selectedTime).finally(() => {
        setIsInitializing(false);
      });
    } else if (availableDates.length > 0) {
      // 날짜는 로드되었지만 조건에 맞지 않는 경우에도 초기화 완료
      setIsInitializing(false);
    }
  }, [availableDates]);

  // 초기 시간 설정 (09:00 ~ 15:30 범위 내에서)
  useEffect(() => {
    if (!isTimeInValidRange(selectedTime)) {
      setSelectedTime('09:00');
    }
  }, []);

  // 날짜가 변경될 때 오늘 날짜인 경우 시간 조정
  useEffect(() => {
    if (isSelectedDateToday(selectedDate) && isTimeAfterNow(selectedTime)) {
      const now = new Date();
      const currentHour = now.getHours().toString().padStart(2, '0');
      const currentMinute = now.getMinutes().toString().padStart(2, '0');
      const currentTimeString = `${currentHour}:${currentMinute}`;
      
      if (isTimeInValidRange(currentTimeString)) {
        setSelectedTime(currentTimeString);
      } else if (now.getHours() < 9) {
        setSelectedTime('09:00');
      } else {
        setSelectedTime('15:30');
      }
    }
  }, [selectedDate]);

  const handlePrevious = () => {
    const [hours, minutes] = selectedTime.split(':').map(Number);
    const currentDate = new Date(selectedDate);
    let shouldFetch = false;
    let newDate = selectedDate;
    let newTime = selectedTime;
    
    switch(timeUnit) {
      case '분':
        // 분만 변경, 날짜와 시간은 고정
        let newMinutes = minutes - 1;
        let newHours = hours;
        
        if (newMinutes < 0) {
          newMinutes = 59;
          newHours = hours - 1;
        }
        
        const newTimeString = `${newHours.toString().padStart(2, '0')}:${newMinutes.toString().padStart(2, '0')}`;
        
        // 시간 제한 확인
        if (isTimeValid(selectedDate, newTimeString)) {
          setSelectedTime(newTimeString);
          newTime = newTimeString;
          shouldFetch = true;
        }
        break;
      case '시':
        // 시간만 변경, 날짜는 고정
        const newHours_h = hours - 1;
        const newTimeString_h = `${newHours_h.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
        
        // 시간 제한 확인
        if (newHours_h >= 0 && isTimeValid(selectedDate, newTimeString_h)) {
          setSelectedTime(newTimeString_h);
          newTime = newTimeString_h;
          shouldFetch = true;
        }
        break;
      case '일':
        // 날짜만 변경, 시간은 그대로
        const prevDate = new Date(currentDate);
        prevDate.setDate(prevDate.getDate() - 1);
        const prevDateString = prevDate.toISOString().split('T')[0];
        setSelectedDate(prevDateString);
        newDate = prevDateString;
        shouldFetch = true;
        break;
    }
    
    // 값이 변경되었으면 자동으로 조회
    if (shouldFetch) {
      // 약간의 지연을 두어 상태 업데이트가 완료된 후 조회
      setTimeout(() => {
        fetchTradingData(newDate, newTime);
      }, 50);
    }
  };

  const handleNext = () => {
    const [hours, minutes] = selectedTime.split(':').map(Number);
    const currentDate = new Date(selectedDate);
    let shouldFetch = false;
    let newDate = selectedDate;
    let newTime = selectedTime;
    
    switch(timeUnit) {
      case '분':
        // 분만 변경, 날짜와 시간은 고정
        let newMinutes_n = minutes + 1;
        let newHours_n = hours;
        
        if (newMinutes_n >= 60) {
          newMinutes_n = 0;
          newHours_n = hours + 1;
        }
        
        const newTimeString_n = `${newHours_n.toString().padStart(2, '0')}:${newMinutes_n.toString().padStart(2, '0')}`;
        
        // 시간 제한 확인
        if (newHours_n < 24 && isTimeValid(selectedDate, newTimeString_n)) {
          setSelectedTime(newTimeString_n);
          newTime = newTimeString_n;
          shouldFetch = true;
        }
        break;
      case '시':
        // 시간만 변경, 날짜는 고정
        const newHours_nh = hours + 1;
        const newTimeString_nh = `${newHours_nh.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
        
        // 시간 제한 확인
        if (newHours_nh < 24 && isTimeValid(selectedDate, newTimeString_nh)) {
          setSelectedTime(newTimeString_nh);
          newTime = newTimeString_nh;
          shouldFetch = true;
        }
        break;
      case '일':
        // 날짜만 변경, 시간은 그대로
        const nextDate = new Date(currentDate);
        nextDate.setDate(nextDate.getDate() + 1);
        const nextDateString = nextDate.toISOString().split('T')[0];
        setSelectedDate(nextDateString);
        newDate = nextDateString;
        shouldFetch = true;
        break;
    }
    
    // 값이 변경되었으면 자동으로 조회
    if (shouldFetch) {
      // 약간의 지연을 두어 상태 업데이트가 완료된 후 조회
      setTimeout(() => {
        fetchTradingData(newDate, newTime);
      }, 50);
    }
  };

  const handleSearch = () => {
    // 유효성 검사 - 조회 버튼 클릭 시에만 실행
    if (!isTimeInValidRange(selectedTime)) {
      alert('거래 시간(09:00 ~ 15:30) 범위 내의 시간만 선택할 수 있습니다.');
      return;
    }

    if (isSelectedDateToday(selectedDate) && isTimeAfterNow(selectedTime)) {
      const now = new Date();
      const currentHour = now.getHours().toString().padStart(2, '0');
      const currentMinute = now.getMinutes().toString().padStart(2, '0');
      alert(`현재 시간(${currentHour}:${currentMinute})까지만 선택할 수 있습니다.\n미래 시간은 선택할 수 없습니다.`);
      return;
    }

    console.log('조회 요청:', { selectedDate, selectedTime, timeUnit });
    // API 호출
    fetchTradingData(selectedDate, selectedTime);
  };

  // 상위 15개 테마를 거래대금 순으로 추출하는 함수 (ThemePage와 동일)
  const extractTop15Themes = (data) => {
    const themeMap = new Map();

    // 각 테마별 거래대금 합산 계산
    data.forEach(item => {
      if (item.themes && Array.isArray(item.themes)) {
        item.themes.forEach(theme => {
          const themeName = theme?.trim();
          if (themeName) {
            const tradingValueRaw = item.tradingValueRaw || 0;
            const tradingValue = tradingValueRaw * 1000000; // API 원본값에 100만원만 곱하기

            if (!themeMap.has(themeName)) {
              themeMap.set(themeName, 0);
            }
            themeMap.set(themeName, themeMap.get(themeName) + tradingValue);
          }
        });
      }
    });

    // 거래대금 순으로 정렬하여 상위 15개 반환
    return Array.from(themeMap.entries())
      .sort((a, b) => b[1] - a[1]) // 거래대금 내림차순 정렬
      .slice(0, 15) // 상위 15개만
      .map(([themeName]) => themeName); // 테마명만 추출
  };

  // 거래대금 데이터 조회 함수
  const fetchTradingData = async (date, time) => {
    try {
      setIsSearching(true);
      setIsInitializing(false); // 데이터 조회 시작하면 초기화 완료
      // 이전 데이터 유지 - 깜빡임 방지

      const server = getServer('stock-api-server');

      if (!server) {
        console.error('stock-api-server를 찾을 수 없습니다.');
        setTradingData([]); // 서버 없으면 표 비우기
        return;
      }

      if (!server.isActive) {
        console.error('stock-api-server가 비활성 상태입니다.');
        setTradingData([]); // 서버 비활성이면 표 비우기
        return;
      }

      const apiService = createApiService(server);

      // 날짜를 yyyyMMdd 형태로 변환
      const formattedDate = date.replace(/-/g, '');

      // 시간을 HHmm 형태로 변환
      const formattedTime = time.replace(':', '');

      // API 호출
      const response = await apiService.get(`/api/v1/ranks/${formattedDate}`, {
        time: formattedTime,
        offset: 0,
        limit: 100
      });

      console.log('거래대금 데이터 조회 성공:', response);
      const rawData = response.data || response;

      // 데이터가 없거나 빈 배열이면 표 비우기
      if (!rawData || (Array.isArray(rawData) && rawData.length === 0)) {
        console.log('조회 결과 데이터 없음 - 표 비우기');
        setTradingData([]);
        setAllAvailableThemes([]);
        return;
      }

      const transformedData = Array.isArray(rawData) ? rawData.map(transformApiData) : [];
      const filteredData = filterStockData(transformedData);

      // 상위 15개 테마 추출 (거래대금 순)
      const top15Themes = extractTop15Themes(filteredData);
      setAllAvailableThemes(top15Themes);

      // rank 기준 오름차순 정렬 (원본 배열을 변경하지 않도록 복사 후 정렬)
      const sortedData = [...filteredData].sort((a, b) => {
        const rankA = parseInt(a.rank) || 0;
        const rankB = parseInt(b.rank) || 0;
        return rankA - rankB;
      });

      // 필터 적용 후 데이터가 없으면 표 비우기
      if (sortedData.length === 0) {
        console.log('필터 적용 후 데이터 없음 - 표 비우기');
      }

      setTradingData(sortedData);

    } catch (error) {
      console.error('거래대금 데이터 조회 실패:', error);
      // 에러 시 표 비우기
      setTradingData([]);
      setAllAvailableThemes([]);
      alert(`데이터 조회에 실패했습니다: ${error.message || '네트워크 오류'}`);
    } finally {
      setIsSearching(false);
    }
  };


  return (
    <div className="page-container">
      {/* 날짜/시간 선택 및 제어 패널 */}
      <div className="datetime-control-panel">
        <div className="datetime-inputs">
          <div className="input-group">
            <label>날짜</label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => handleDateChange(e.target.value)}
              className={`date-input ${!isDateAvailable(selectedDate) ? 'invalid-date' : ''}`}
              title={!isDateAvailable(selectedDate) ? '조회할 수 없는 날짜입니다' : ''}
            />
          </div>
          
          <div className="input-group">
            <label>시간</label>
            <input
              type="time"
              value={selectedTime}
              onChange={(e) => handleTimeChange(e.target.value)}
              min="09:00"
              max={isSelectedDateToday(selectedDate) ? 
                (() => {
                  const now = new Date();
                  const currentHour = now.getHours().toString().padStart(2, '0');
                  const currentMinute = now.getMinutes().toString().padStart(2, '0');
                  return `${currentHour}:${currentMinute}`;
                })() : '15:30'
              }
              className={`time-input ${!isTimeValid(selectedDate, selectedTime) ? 'invalid-time' : ''}`}
              title={
                !isTimeValid(selectedDate, selectedTime) 
                  ? (isSelectedDateToday(selectedDate) && isTimeAfterNow(selectedTime)
                    ? '현재 시간까지만 선택 가능합니다'
                    : '09:00 ~ 15:30 시간대만 선택 가능합니다')
                  : ''
              }
            />
          </div>
          
          <div className="input-group">
            <label>단위</label>
            <select
              value={timeUnit}
              onChange={(e) => setTimeUnit(e.target.value)}
              className="unit-select"
            >
              <option value="분">분</option>
              <option value="시">시</option>
              <option value="일">일</option>
            </select>
          </div>
        </div>
        
        <div className="control-buttons">
          <button onClick={handlePrevious} className="nav-btn prev-btn">
            &#8249;
          </button>
          <button onClick={handleNext} className="nav-btn next-btn">
            &#8250;
          </button>
          <button 
            onClick={handleSearch} 
            className="search-btn"
            disabled={loading || isSearching || !isDateAvailable(selectedDate) || !isTimeValid(selectedDate, selectedTime)}
          >
            {isSearching ? '조회 중...' : '조회'}
          </button>
          <button 
            onClick={() => setShowSettings(true)} 
            className="settings-btn"
            style={{ 
              background: 'none', 
              border: 'none', 
              fontSize: '18px', 
              cursor: 'pointer',
              padding: '8px',
              borderRadius: '4px'
            }}
            title="설정"
          >
            ⚙️
          </button>
        </div>
      </div>

      <TradingTable
        key="realtime-page-trading-table"
        pageId="RealtimePage"
        data={tradingData}
        isLoading={isInitializing}
        isSearching={isSearching}
        loadingMessage="데이터를 조회하고 있습니다..."
        initialLoadingMessage="초기 데이터를 로딩하고 있습니다..."
        showSettings={showSettings}
        setShowSettings={setShowSettings}
        visibleDetails={visibleDetails}
        setVisibleDetails={setVisibleDetails}
        chartSettings={chartSettings}
        setChartSettings={setChartSettings}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        selectedDate={selectedDate}
        showDetailsSection={showDetailsSection}
        setShowDetailsSection={handleSetShowDetailsSection}
        topThemes={allAvailableThemes}
      />

      <div className="last-updated">
        <span>마지막 업데이트: {new Date().toLocaleString()}</span>
      </div>
    </div>
  );
};

export default RealtimePage;